import { useCallback, useEffect, useState } from 'react'
import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { faEdit, faEnvelope, faMobile, faPhone, faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { CustomerContactPersonModelType } from '@teinor/erp/types/company/customer/customerContactPerson'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import useEditDeleteModal from '../../../../../../utils/hooks/useEditDeleteModal'
import PermissionChecker from '../../../../../../baseComponents/PermissionChecker'
import TwinIcon, { TwinIconProp, TwinIconToolTip } from '../../../../../../baseComponents/TwinIcon'
import ModalCreateCustomerContactPerson from '../../../CustomerContactsPersons/ModalCreateCustomerContactPerson'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import ModalEditCustomerContactPerson from '../../../CustomerContactsPersons/ModalEditCustomerContactPerson'
import ModalChangeActiveCustomerContactPerson from '../../../CustomerContactsPersons/ModalChangeActiveCustomerContactPerson'
import { OnSubmit } from '../../../../../../forms/TwinForm/types'
import { CustomerTypesInversedKeys } from '@teinor/erp/types/company/customer/customerTypes'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { CustomerDashboardAddButton, CustomerDashboardSeeAllButton } from '../../SpecificComponents'
import { getUserPermissions } from '../../../../../../utils/reducers/getters'
import { DisplayCustomerInfoToolTip } from '../../../../CustomerCustomSelectContactInfo'

interface CustomerContactPersonsDashboardProps extends CustomerContactPersonsDashboardLogicProps { 
    title: string
}

const CustomerContactPersonsDashboard: React.FC<CustomerContactPersonsDashboardProps> = ({ allRowData, title }) => {
    const { t } = useTwinTranslation()
    const { data, getData } = useCustomerContactPersonsDashboardLogic({ allRowData })
    const userPermissions = getUserPermissions('customer.profile.contactPerson')
    return (
        <div className='flex flex-auto flex-col gap-8 overflow-hidden'>
            <div className='flex flex-wrap flex-row justify-between gap-5'>
                <h3>{title}</h3>
                <CustomerDashboardAddButton CreateModalComponent={ModalCreateCustomerContactPerson} onSubmit={getData} userPermissions={userPermissions || { n: 4, permission: 'delete' }} text={t('newPerson', 'Nueva persona')} extraModalData={{CustomerId: allRowData?.id}} />
            </div>
            <CustomerContactPersonsList customerType={allRowData?.customer_type} allRowData={data} onSubmit={getData} userPermissions={userPermissions || { n: 1, permission: 'read' }} />
        </div>
    )
}

interface CustomerContactPersonsDashboardLogicProps {
    allRowData: CustomerModelType
}

const useCustomerContactPersonsDashboardLogic = ({ allRowData }: CustomerContactPersonsDashboardLogicProps) => {
    const [data, setData] = useState<any | null>(null)

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerContactPerson/getAllCustomerContactPersons', {
            limit: 30,
            offset: 0,
            order: [["id", "desc"]],
            where: {
                CustomerId: allRowData?.id,
                '$or': { active: true }
            }
        })
        if (result) {
            setData(result)
        }
    }, [setData, allRowData?.id])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, getData }
}

interface CustomerContactPersonsListProps extends ComponentWithPermissions {
    allRowData: CustomerContactPersonModelType[]
    customerType: CustomerTypesInversedKeys
    onSubmit: OnSubmit
 }

const CustomerContactPersonsList: React.FC<CustomerContactPersonsListProps> = ({ allRowData, onSubmit, userPermissions, customerType }: CustomerContactPersonsListProps) => {
    const { t } = useTwinTranslation()
    const renderThis: JSX.Element[] = []

    if (allRowData) {
        for (const contact of allRowData) {
            renderThis.push(<SingleContactPerson allRowData={contact} onSubmit={onSubmit} userPermissions={userPermissions} key={contact.id} />)
        }
    }

    return (
        <div className='flex flex-col flex-auto h-1 gap-y-8'>
            <div className='flex flex-auto h-1 w-full flex-col overflow-auto pr-10 items-center'>
                {renderThis.length ? renderThis : <span className='italic text-gray-9D pl-1 mt-30'>{t('noPeople', 'No hay personas')}</span>}
            </div>
            <CustomerDashboardSeeAllButton customerType={customerType} customerTab='people' text={t('SeeAllPeople', 'Ver todas las personas')} />
        </div>
    )
}

type SingleContactPersonProps = & EditDeleteSingleContactButtonsProps & ComponentWithPermissions & {}

const SingleContactPerson: React.FC<SingleContactPersonProps> = ({ allRowData, onSubmit, userPermissions }) => {
    return (
        <div className='flex flex-row w-full items-center justify-between border-b border-gray-EE gap-x-10'>
            <div className='flex flex-col flex-auto py-5 justify-center min-h-40 w-1'>
                <div className='flex min-w-130 cursor-pointer items-center twin_elipsis' id={'contact' + allRowData?.id}>
                    {allRowData?.name}
                </div>
                <SingleContactPersonExtraData data={allRowData?.email} icon={faEnvelope} />
                <SingleContactPersonExtraData data={allRowData?.phone || allRowData?.mobile} icon={allRowData?.phone ? faPhone : faMobile} />
            </div>
            <EditDeleteSingleContactButtons allRowData={allRowData} onSubmit={onSubmit} userPermissions={userPermissions} />
            {allRowData ? <DisplayCustomerInfoToolTip id={String(allRowData?.id)} selectedContact={allRowData} /> : null}
        </div>
    )
}

interface SingleContactPersonExtraDataProps {
    data: string | undefined
    icon: TwinIconProp
}

const SingleContactPersonExtraData: React.FC<SingleContactPersonExtraDataProps> = ({ data, icon }) => {
    if (!data) {
        return null
    }
    return (
        <div className = 'inline-block ml-1 light12 text-gray-97 cursor-pointer twin_elipsis'><TwinIcon icon={icon} className='mr-10'/>{data}</div >
    )
}


interface EditDeleteSingleContactButtonsProps extends ComponentWithPermissions {
    allRowData: CustomerContactPersonModelType
    onSubmit: OnSubmit
}

const EditDeleteSingleContactButtons: React.FC<EditDeleteSingleContactButtonsProps> = ({ allRowData, onSubmit, userPermissions }) => {
    const { t } = useTwinTranslation()
    const { setOpenModal, openModal } = useEditDeleteModal()
    return (
        <div className='flex flex-row mt-4 py-5 h-full gap-x-15'>
            <PermissionChecker permission='update' userPermissions={userPermissions}>
                <div onClick={() => setOpenModal({ type: 'edit', allRowData })}>
                    <TwinIconToolTip icon={faEdit} className='cursor-pointer regular16 hover:text-green-43' message={t('edit', 'Editar')} id={'editIcon' + allRowData?.id} />
                </div>
            </PermissionChecker>
            <PermissionChecker permission='delete' userPermissions={userPermissions}>
                <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                    <TwinIconToolTip icon={faTrashCan} className='cursor-pointer regular16 hover:text-red-BA' message={t('delete', 'Eliminar')} id={'deleteIcon' + allRowData?.id} />
                </div>
            </PermissionChecker>
            {openModal?.type === 'edit' ? <ModalEditCustomerContactPerson onSubmit={onSubmit} setOpened={() => setOpenModal(null)} userPermissions={{ n: 4, permission: 'delete' }} {...{ allRowData }} /> : null}
            {openModal?.type === 'delete' ? <ModalChangeActiveCustomerContactPerson onSubmit={onSubmit} setOpened={() => setOpenModal(null)} {...{ allRowData }} /> : null}
        </div>
    )
}

export default CustomerContactPersonsDashboard
